import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  resolveWorkspaceAgentBoardTreeExpansion,
  toggleWorkspaceAgentBoardTreeExpansion,
  type WorkspaceAgentBoardTreeExpansion,
  type WorkspaceAgentBoardTreeExpansionOverrides,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/flattenWorkspaceAgentBoardTree';

const EMPTY_EXPANSION_OVERRIDES: WorkspaceAgentBoardTreeExpansionOverrides = {};

const EMPTY_INITIAL_EXPANDED_NODE_IDS: readonly string[] = [];

const toInitialExpansionOverrides = (nodeIds: readonly string[]): WorkspaceAgentBoardTreeExpansionOverrides => {
  if (nodeIds.length === 0) {
    return EMPTY_EXPANSION_OVERRIDES;
  }

  return Object.fromEntries(nodeIds.map((nodeId) => [nodeId, true]));
};

/** Overrides stamped with the query they were made against, so a new query starts from the matches. */
type WorkspaceAgentBoardTreeSearchExpansion = {
  overrides: WorkspaceAgentBoardTreeExpansionOverrides;
  query: string;
};

export type UseWorkspaceAgentBoardTreeExpansionParams = {
  /**
   * Rows to open on mount, over the default. Read once: a later change would reopen a node the
   * user had closed. Callers that need a starting path mount with it already known.
   */
  initialExpandedNodeIds?: readonly string[];
  /**
   * True for the whole picker session, not just while Boards is the active chip. What the user
   * opened in the tree belongs to that session, so only the session ending may take it away — a
   * trip to the Assets chip and back must find the same boards open.
   */
  isPickerOpen: boolean;
  query: string;
};

/** What the user has opened and closed, browsing and searching, and the one toggle that writes it. */
export const useWorkspaceAgentBoardTreeExpansion = ({
  initialExpandedNodeIds = EMPTY_INITIAL_EXPANDED_NODE_IDS,
  isPickerOpen,
  query,
}: UseWorkspaceAgentBoardTreeExpansionParams) => {
  /**
   * What the user has opened or closed, over the default rather than a snapshot of it — a library
   * that arrives after the first toggle must still start open.
   */
  const [expansionOverrides, setExpansionOverrides] = useState<WorkspaceAgentBoardTreeExpansionOverrides>(() =>
    toInitialExpansionOverrides(initialExpandedNodeIds),
  );

  /**
   * Searching keeps its own overrides, stamped with the query they were made against: a match is
   * force-opened, and neither a node the user collapsed while browsing nor one they collapsed under
   * an earlier query may hide the hits found under this one.
   */
  const [searchExpansion, setSearchExpansion] = useState<WorkspaceAgentBoardTreeSearchExpansion>({
    overrides: EMPTY_EXPANSION_OVERRIDES,
    query: '',
  });

  const searchExpansionOverrides =
    searchExpansion.query === query ? searchExpansion.overrides : EMPTY_EXPANSION_OVERRIDES;

  // `toggleExpanded` is handed to the popup and must stay stable, so it reads the query through a ref.
  const queryRef = useRef(query);
  queryRef.current = query;

  const isExpanded = useMemo<WorkspaceAgentBoardTreeExpansion>(
    () => resolveWorkspaceAgentBoardTreeExpansion(expansionOverrides),
    [expansionOverrides],
  );

  const isSearchExpanded = useMemo<WorkspaceAgentBoardTreeExpansion>(
    () => resolveWorkspaceAgentBoardTreeExpansion(searchExpansionOverrides),
    [searchExpansionOverrides],
  );

  const wasPickerOpenRef = useRef(isPickerOpen);

  /**
   * A closed picker replaces the tree; stale open rows would refetch children nobody sees. On the
   * session's falling edge only: `isPickerOpen` is true for the whole picker session, so a trip to
   * the Assets chip is not one, and the tree is not collapsed on the way there.
   */
  useEffect(() => {
    const wasPickerOpen = wasPickerOpenRef.current;

    wasPickerOpenRef.current = isPickerOpen;

    if (wasPickerOpen && !isPickerOpen) {
      setExpansionOverrides(EMPTY_EXPANSION_OVERRIDES);
      setSearchExpansion({ overrides: EMPTY_EXPANSION_OVERRIDES, query: '' });
    }
  }, [isPickerOpen]);

  const toggleExpanded = useCallback((nodeId: string, isNodeExpanded: boolean) => {
    const { current: currentQuery } = queryRef;

    if (currentQuery === '') {
      setExpansionOverrides((current) => toggleWorkspaceAgentBoardTreeExpansion(current, nodeId, isNodeExpanded));

      return;
    }

    setSearchExpansion((current) => ({
      overrides: toggleWorkspaceAgentBoardTreeExpansion(
        current.query === currentQuery ? current.overrides : EMPTY_EXPANSION_OVERRIDES,
        nodeId,
        isNodeExpanded,
      ),
      query: currentQuery,
    }));
  }, []);

  return { expansionOverrides, isExpanded, isSearchExpanded, toggleExpanded };
};
